import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalOverlay, ModalHeader, ModalFooter } from "@chakra-ui/modal";
import { deleteOrganization } from "api/graphQL_organizations";
import DivLoader from "components/divloader/DivLoader";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { MdDelete, MdHighlightOff, MdWarning } from "react-icons/md";
import { Select as AntSelect } from "antd";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

const DeleteOrgModal = ({
  name,
  id,
  fetchOrganizations,
  fetchAllOrganizations,
  clients,
  allOrg,
}: {
  name: string;
  id: string;
  fetchOrganizations: () => void;
  fetchAllOrganizations: () => void;
  clients: any;
  allOrg: any;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCloseModal, setIsCloseModal] = useState<boolean>(false);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [orgError, setOrgError] = useState("");
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
    
  const filteredOrg = allOrg.filter((or: any) => or.name !== name);

  const handleCredentialSelect = (event: any) => {
    setSelectedOrg(event);
    setOrgError("");
  };

  const handleClose = () => {
    setSelectedOrg("");
    setOrgError("");
    setConfirmed(false);
    setIsCloseModal(true);
    setTimeout(() => {
      setIsCloseModal(false);
      onClose();
    }, 300);
  };

  const handleDelete = () => {
    if (clients.length > 0 && selectedOrg === "") {
      setOrgError("Please select an organization to reassign clients");
      return;
    }
    setIsLoading(true);
    deleteOrganization(id, selectedOrg)
      .then(() => {
        fetchOrganizations();
        fetchAllOrganizations();
        toast.success("Organization deleted successfully");
        handleClose();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message ?? "Organization deletion failed");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const hasClients = clients && clients.length > 0;
  const hasOtherOrgs = filteredOrg.length > 0;

  return (
    <>
      {/* Trigger icon */}
      <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
        <PopoverTrigger>
          <button onClick={onOpen} className="org-del-trigger" aria-label="Delete organization" onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}>
            <MdDelete size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          border="1px solid #fecaca"
          borderRadius="12px"
          boxShadow="0 8px 24px rgba(15,23,42,0.08)"
          bg="#fef2f2"
          maxW="260px"
          p="0"
          overflow="hidden"
          zIndex="20"
        >
          <PopoverHeader
            bg="#fee2e2"
            fontSize="12px"
            fontWeight="700"
            color="#b91c1c"
            borderBottom="1px solid #fecaca"
            px="14px"
            py="10px"
          >
            Delete Organization
          </PopoverHeader>
          <PopoverArrow bg="#fef2f2" />
        </PopoverContent>
      </Popover>

      {/* Modal / slide panel */}
      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`odm-panel${isOpen && !isCloseModal ? " odm-panel--open" : " odm-panel--close"}`}>

          {/* Header */}
          <div className="odm-header">
            <div className="odm-header-left">
              <div className="odm-header-icon">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14H6L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4h6v2"/>
                </svg>
              </div>
              <div>
                <h2 className="odm-header-title">Delete Organization</h2>
                <p className="odm-header-sub">This action cannot be undone</p>
              </div>
            </div>
            <button className="odm-close-btn" onClick={handleClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="odm-body">

            {hasClients ? (
              /* Has clients scenario */
              <div className="odm-clients-wrap">
                {hasOtherOrgs ? (
                  <>
                    {/* Warning */}
                    <div className="odm-warning-card">
                      <div className="odm-warning-icon-wrap">
                        <MdWarning size={22} />
                      </div>
                      <div className="odm-warning-content">
                        <p className="odm-warning-title">Clients need to be reassigned</p>
                        <p className="odm-warning-desc">
                          <strong>{name}</strong> is currently assigned to {clients.length} client{clients.length !== 1 ? "s" : ""}. Select another organization to reassign them before deleting.
                        </p>
                      </div>
                    </div>

                    {/* Client list */}
                    <div className="odm-client-list-card">
                      <p className="odm-client-list-title">Assigned Clients ({clients.length})</p>
                      <div className="odm-client-list">
                        {clients.map((client: any) => (
                          <div key={client.id} className="odm-client-item">
                            <div className="odm-client-avatar">
                              {client.name?.charAt(0)?.toUpperCase() || "C"}
                            </div>
                            <span className="odm-client-name">{client.name}</span>
                            <span className="odm-client-id">{client.id}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Reassign select */}
                    <div className="odm-reassign-wrap">
                      <label className="odm-reassign-label">
                        Reassign clients to <span className="odm-req">*</span>
                      </label>
                      <AntSelect
                        showSearch
                        placeholder="Select organization to reassign…"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          String(option?.label ?? "").toUpperCase().includes(input.toUpperCase())
                        }
                        filterSort={(a, b) =>
                          String(a?.label ?? "").toLowerCase().localeCompare(String(b?.label ?? "").toLowerCase())
                        }
                        className="odm-ant-select"
                        style={{ width: "100%" }}
                        options={filteredOrg.map((or: any) => ({
                          value: or?.id,
                          label: String(or?.name),
                        }))}
                        value={selectedOrg || undefined}
                        onChange={handleCredentialSelect}
                        notFoundContent="No organizations found"
                        status={orgError ? "error" : ""}
                      />
                      {orgError && (
                        <p className="odm-field-err">{orgError}</p>
                      )}
                    </div>
                  </>
                ) : (
                  /* No other orgs exist */
                  <div className="odm-warning-card odm-warning-card--block">
                    <div className="odm-warning-icon-wrap">
                      <MdWarning size={22} />
                    </div>
                    <div className="odm-warning-content">
                      <p className="odm-warning-title">Cannot delete at this time</p>
                      <p className="odm-warning-desc">
                        Before deleting <strong>{name}</strong>, please create another organization to reassign the {clients.length} existing client{clients.length !== 1 ? "s" : ""}. No other organizations are currently available.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* No clients — standard delete warning */
              <>
                <div className="odm-warning-card">
                  <div className="odm-warning-icon-wrap">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                  <div className="odm-warning-content">
                    <p className="odm-warning-title">Irreversible Action</p>
                    <p className="odm-warning-desc">
                      Deleting <strong>{name}</strong> will permanently remove all associated data including gateway configurations and settings. This cannot be recovered.
                    </p>
                  </div>
                </div>

                {/* What gets deleted */}
                <div className="odm-consequences">
                  <p className="odm-consequences-title">What will be deleted:</p>
                  <div className="odm-consequence-list">
                    {[
                      "Organization profile and settings",
                      "All gateway assignments",
                      "Payment method configurations",
                      "Associated routing rules",
                    ].map((item, i) => (
                      <div key={i} className="odm-consequence-item">
                        <div className="odm-consequence-dot" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Confirm checkbox */}
                <label className="odm-confirm-row">
                  <input
                    type="checkbox"
                    className="odm-confirm-check"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span className="odm-confirm-label">
                    I understand this action is permanent and cannot be undone
                  </span>
                </label>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="odm-footer">
            <div className="odm-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Confirm your intent before proceeding
            </div>
            <div className="odm-footer-btns">
              <button className="odm-btn-cancel" onClick={handleClose}>
                Cancel
              </button>

              {hasClients ? (
                hasOtherOrgs ? (
                  <button
                    className="odm-btn-delete"
                    onClick={handleDelete}
                    disabled={!!isLoading}
                  >
                    {isLoading ? (
                      <span className="odm-spinner" />
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14H6L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/>
                        </svg>
                        Reassign &amp; Delete
                      </>
                    )}
                  </button>
                ) : null
              ) : (
                <button
                  className={`odm-btn-delete${confirmed ? "" : " odm-btn-delete--disabled"}`}
                  onClick={handleDelete}
                  disabled={!confirmed || !!isLoading}
                >
                  {isLoading ? (
                    <span className="odm-spinner" />
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14H6L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                      </svg>
                      Delete Organization
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* Trigger */
        .org-del-trigger {
          width: 32px; height: 32px; border-radius: 8px;
          background: #fff7f7; border: 1.5px solid #fecaca;
          color: #dc2626; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all .15s;
        }
        .org-del-trigger:hover { background: #fee2e2; border-color: #fca5a5; }

        /* Slide panel */
        .odm-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 600px;
          background: #ffffff;
          z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -8px 0 40px rgba(15,23,42,0.15);
          transition: transform .32s cubic-bezier(0.16,1,0.3,1);
        }
        .odm-panel--open  { transform: translateX(0); }
        .odm-panel--close { transform: translateX(100%); }

        /* Header */
        .odm-header {
          padding: 16.5px 28px;
          background: linear-gradient(135deg, #7f1d1d, #dc2626);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .odm-header-left { display: flex; align-items: center; gap: 14px; }
        .odm-header-icon {
          width: 42px; height: 42px; border-radius: 11px;
          background: rgba(255,255,255,.18); border: 1.5px solid rgba(255,255,255,.28);
          display: flex; align-items: center; justify-content: center;
          color: #fff; flex-shrink: 0;
        }
        .odm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 22px; }
        .odm-header-sub { font-size: 12.5px; color: rgba(255,255,255,.65); font-weight: 500; }
        .odm-close-btn {
          background: rgba(255,255,255,.15); border: none; border-radius: 8px;
          width: 32px; height: 32px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,.8); transition: background .15s;
        }
        .odm-close-btn:hover { background: rgba(255,255,255,.25); color: #fff; }

        /* Body */
        .odm-body { flex: 1; padding: 28px; display: flex; flex-direction: column; gap: 20px; overflow-y: auto; }
        .odm-body::-webkit-scrollbar { width: 4px; }
        .odm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* Warning card */
        .odm-warning-card {
          display: flex; gap: 14px;
          background: linear-gradient(135deg, #fff7f7, #fee2e2);
          border: 1.5px solid #fecaca; border-radius: 14px; padding: 18px;
        }
        .odm-warning-card--block { flex-direction: column; }
        .odm-warning-icon-wrap {
          width: 44px; height: 44px; border-radius: 11px;
          background: #fff; border: 1.5px solid #fecaca;
          display: flex; align-items: center; justify-content: center;
          color: #dc2626; flex-shrink: 0;
        }
        .odm-warning-content { flex: 1; }
        .odm-warning-title { font-size: 14px; font-weight: 700; color: #991b1b; margin-bottom: 5px;line-height:5px; }
        .odm-warning-desc { font-size: 13px; color: #b91c1c; line-height: 1.55; font-weight: 500; }

        /* Client list */
        .odm-clients-wrap { display: flex; flex-direction: column; gap: 16px; }
        .odm-client-list-card { background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
        .odm-client-list-title { font-size: 12.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 12px;line-height:5px; }
        .odm-client-list { display: flex; flex-direction: column; gap: 6px; max-height: 200px; overflow-y: auto; }
        .odm-client-list::-webkit-scrollbar { width: 3px; }
        .odm-client-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .odm-client-item { display: flex; align-items: center; gap: 10px; padding: 8px; background: #fff; border-radius: 8px; border: 1px solid #f1f5f9; }
        .odm-client-avatar { width: 28px; height: 28px; border-radius: 7px; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #2563eb; flex-shrink: 0; }
        .odm-client-name { font-size: 13px; font-weight: 600; color: #0f172a; flex: 1; }
        .odm-client-id { font-size: 11px; color: #94a3b8; font-weight: 500; }

        /* Reassign */
        .odm-reassign-wrap { display: flex; flex-direction: column; gap: 6px; }
        .odm-reassign-label { font-size: 12.5px; font-weight: 700; color: #374151; }
        .odm-req { color: #dc2626; }
        .odm-ant-select .ant-select-selector { height: 46px !important; border: 1.5px solid #e2e8f0 !important; border-radius: 11px !important; background: #f8fafc !important; font-family: 'Plus Jakarta Sans', sans-serif !important; }
        .odm-ant-select .ant-select-selection-search-input { height: 44px !important; }
        .odm-ant-select .ant-select-selection-placeholder { line-height: 44px !important; color: #cbd5e1 !important; }
        .odm-ant-select .ant-select-selection-item { line-height: 44px !important; }
        .odm-field-err { font-size: 12px; color: #dc2626; font-weight: 500; margin-top: 10px; }

        /* Consequences */
        .odm-consequences { background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
        .odm-consequences-title { font-size: 12.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 12px; }
        .odm-consequence-list { display: flex; flex-direction: column; gap: 8px; }
        .odm-consequence-item { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: #475569; font-weight: 500; }
        .odm-consequence-dot { width: 6px; height: 6px; border-radius: 50%; background: #ef4444; flex-shrink: 0; }

        /* Confirm checkbox */
        .odm-confirm-row {
          display: flex; align-items: flex-start; gap: 12px; cursor: pointer;
          background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 12px; padding: 14px;
        }
        .odm-confirm-check { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; accent-color: #dc2626; cursor: pointer; }
        .odm-confirm-label { font-size: 13px; font-weight: 600; color: #92400e; line-height: 1.5; }

        /* Footer */
        .odm-footer {
          padding: 16px 28px; border-top: 1px solid #f1f5f9; background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; flex-shrink: 0;
        }
        .odm-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .odm-footer-btns { display: flex; gap: 10px; }

        .odm-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600; color: #64748b;
          padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .odm-btn-cancel:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }

        .odm-btn-delete {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #991b1b, #dc2626);
          cursor: pointer; box-shadow: 0 4px 14px rgba(220,38,38,.3);
          transition: transform .15s, box-shadow .18s, opacity .18s;
        }
        .odm-btn-delete:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(220,38,38,.38); }
        .odm-btn-delete--disabled { opacity: .45; cursor: not-allowed; transform: none !important; box-shadow: none !important; }

        .odm-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: odmSpin .65s linear infinite; }
        @keyframes odmSpin { to{transform:rotate(360deg)} }

        @media (max-width: 520px) {
          .odm-panel { max-width: 100%; }
          .odm-header, .odm-body, .odm-footer { padding-left: 20px; padding-right: 20px; }
        }
      `}</style>
    </>
  );
};

export default DeleteOrgModal;